import React from 'react';
import { motion } from 'motion/react';
import {
  Building2,
  User,
  FileText,
  CreditCard,
  ShoppingCart,
  ChevronRight,
  CheckCircle2,
  X,
} from 'lucide-react';

export const START_HERE_TUTORIAL_STEPS = [
  {
    id: 'business',
    title: 'Business Information',
    description: 'This is where your invoice identity comes from. Add your business name, email, and mailing details so the invoice clearly shows who is sending it.',
    tips: [
      'The required fields here are what usually block a first PDF download.',
      'Your logo and phone number are optional, but they help the invoice feel complete.',
    ],
    icon: Building2,
  },
  {
    id: 'client',
    title: 'Client Information',
    description: 'Use this section to identify who the invoice is for. Once these details are filled, the invoice can be shared without editing the PDF later.',
    tips: [
      'Client name and email are the best fields to start with.',
      'Address details are useful if you want the invoice to include a full billing address.',
    ],
    icon: User,
  },
  {
    id: 'invoiceDetails',
    title: 'Invoice Details',
    description: 'Set the due date and any optional notes that should appear on the invoice. This section controls timing and any short message to the client.',
    tips: [
      'The due date is required before you can generate the PDF.',
      'Notes are optional and good for reminders or short payment instructions.',
    ],
    icon: FileText,
  },
  {
    id: 'paymentDetails',
    title: 'Payment Details',
    description: 'Add payment instructions here if you want the invoice to tell clients how to pay. These fields are optional, but if you use them they should be valid.',
    tips: [
      'You can skip this section if you are just exploring the tutorial.',
      'If you enter IBAN, BIC, or sort code values, they should be valid before moving on.',
    ],
    icon: CreditCard,
    optional: true,
  },
  {
    id: 'lineItems',
    title: 'Line Items',
    description: 'This is the core of the invoice. Add the work completed, quantity, rate, and any notes so the totals can calculate correctly.',
    tips: [
      'Each item needs a description and a valid quantity and rate.',
      'Once this section is valid, you are ready to review and download the invoice.',
    ],
    icon: ShoppingCart,
  },
];

const TutorialActionButton = ({
  children,
  onClick,
  icon,
  variant = 'secondary',
  isDark,
}) => {
  const className =
    variant === 'primary'
      ? `group flex items-center space-x-2 px-3 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border border-primary/50 focus:outline-none whitespace-nowrap ${isDark ? 'text-primary' : 'text-[#1a1a1a]'}`
      : `group flex items-center space-x-2 px-3 py-2 rounded-[20px] text-sm font-medium transition-all duration-200 border focus:outline-none whitespace-nowrap ${isDark ? 'border-transparent nav-text hover:border-white/30' : 'border-[#d1d5db] text-[#1a1a1a] hover:border-[#b8bec8]'}`;

  const style =
    variant === 'primary'
      ? {
          background: isDark ? 'rgba(167, 238, 67, 0.12)' : '#edff00',
          backdropFilter: isDark ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: isDark ? 'blur(20px)' : 'none',
          boxShadow: isDark
            ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)'
            : '0 1px 3px rgba(0, 0, 0, 0.12)',
          height: '36px',
          minWidth: 'fit-content',
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }
      : {
          background: isDark ? 'transparent' : '#ffffff',
          height: '36px',
          minWidth: 'fit-content',
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.06)',
        };

  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={className}
      style={style}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.12 }}
    >
      {icon && (
        <div className={`flex h-4 w-4 items-center justify-center rounded-full ${isDark ? 'bg-primary/10' : variant === 'primary' ? 'bg-black/10' : 'bg-[#f3f4f6]'}`}>
          {icon}
        </div>
      )}
      <span>{children}</span>
    </motion.button>
  );
};

const StartHereGuide = ({
  step,
  currentStepIndex,
  totalSteps,
  onNext,
  onSkip,
  onCancel,
  isLastStep,
  isDark,
}) => {
  const StepIcon = step.icon;

  const containerClassName = isDark
    ? 'mb-5 overflow-hidden rounded-2xl border border-primary/25 bg-primary/10'
    : 'mb-5 overflow-hidden rounded-2xl border border-[#d9dde5] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.06)]';

  const headerClassName = isDark
    ? 'border-b border-primary/15 bg-primary/10 px-4 py-3 sm:px-5'
    : 'border-b border-[#e5e7eb] bg-[#f8fafc] px-4 py-3 sm:px-5';

  const iconWrapClassName = isDark
    ? 'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary'
    : 'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#eef2f7] text-[#1a1a1a]';

  const badgeClassName = isDark
    ? 'rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary'
    : 'rounded-full border border-[#d9dde5] bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1a1a1a]';

  const metaTextClassName = isDark ? 'text-xs text-muted-foreground' : 'text-xs text-[#6b7280]';
  const descriptionClassName = isDark
    ? 'max-w-3xl text-sm leading-6 text-muted-foreground'
    : 'max-w-3xl text-sm leading-6 text-[#4b5563]';
  const helperTextClassName = isDark ? 'text-sm text-muted-foreground' : 'text-sm text-[#4b5563]';
  const footerTextClassName = isDark ? 'text-xs text-muted-foreground' : 'text-xs text-[#4b5563]';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={containerClassName}
    >
      <div className={headerClassName}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={iconWrapClassName}>
              <StepIcon className="h-5 w-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={badgeClassName}>
                  Start Here
                </span>
                <span className={metaTextClassName}>
                  Step {currentStepIndex + 1} of {totalSteps}
                </span>
                {step.optional && (
                  <span className={metaTextClassName}>
                    Optional section
                  </span>
                )}
              </div>
              <h4 className="text-base font-semibold text-foreground">
                {step.title}
              </h4>
              <p className={descriptionClassName}>
                {step.description}
              </p>
            </div>
          </div>

          <TutorialActionButton
            onClick={onCancel}
            icon={<X className={`h-3 w-3 ${isDark ? 'nav-text' : 'text-[#1a1a1a]'}`} />}
            isDark={isDark}
          >
            Cancel tutorial
          </TutorialActionButton>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4 sm:px-5">
        <div className="space-y-2">
          {step.tips.map((tip) => (
            <div key={tip} className={`flex items-start gap-2 ${helperTextClassName}`}>
              <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${isDark ? 'text-primary' : 'text-[#1a1a1a]'}`} aria-hidden />
              <span>{tip}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className={footerTextClassName}>
            Use <span className="font-medium text-foreground">Next</span> to validate this section, or <span className="font-medium text-foreground">Skip</span> if you just want to continue exploring.
          </p>

          <div className="flex flex-wrap gap-3 sm:justify-end">
            <TutorialActionButton
              onClick={onSkip}
              isDark={isDark}
            >
              Skip
            </TutorialActionButton>
            <TutorialActionButton
              onClick={onNext}
              variant="primary"
              isDark={isDark}
              icon={
                isLastStep
                  ? <CheckCircle2 className={`h-3 w-3 ${isDark ? 'text-primary' : 'text-[#1a1a1a]'}`} />
                  : <ChevronRight className={`h-3 w-3 ${isDark ? 'text-primary' : 'text-[#1a1a1a]'}`} />
              }
            >
              {isLastStep ? 'Finish tutorial' : 'Next'}
            </TutorialActionButton>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StartHereGuide;
